// Admin-side client for the fuelio-chat worker /admin/* endpoints.
//
// Distinct from the regular admin API client at `./api.ts` (which talks
// to fuelio-api via same-origin /api proxy). fuelio-chat lives at a
// separate domain (chat.fuelio.net or workers.dev) so we hit it
// directly with `ADMIN_BEARER` injected at build time via Vite env vars.
//
// Security model: this SPA already sits behind Cloudflare Access, so
// anyone reading the bundle is already an authenticated admin. The
// bearer here just gates the worker against random scripts on the
// public internet — not against admins themselves.

const BASE = ((import.meta.env.VITE_FUELIO_CHAT_URL as string | undefined) ?? '').replace(/\/$/, '')
const BEARER = import.meta.env.VITE_FUELIO_CHAT_ADMIN_BEARER as string | undefined

export class ChatApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`chat_api_${status}`)
  }
}

/** Get the admin email from session for assigning escalations. The fuelio-api
 *  admin login flow already provides this via the whoami call; we read it via
 *  the existing api client. To avoid a circular dependency, callers pass it in. */
async function req<T = unknown>(
  path: string,
  opts: { method?: string; query?: Record<string, string | number | undefined>; body?: unknown; adminEmail?: string } = {},
): Promise<T> {
  if (!BASE) throw new Error('VITE_FUELIO_CHAT_URL not configured')
  if (!BEARER) throw new Error('VITE_FUELIO_CHAT_ADMIN_BEARER not configured')

  // Build query string for filters / pagination.
  const qs = new URLSearchParams()
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v != null) qs.set(k, String(v))
    }
  }
  const url = `${BASE}${path}${qs.toString() ? '?' + qs.toString() : ''}`

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers: {
      authorization: `Bearer ${BEARER}`,
      'content-type': 'application/json',
      ...(opts.adminEmail ? { 'X-Admin-Email': opts.adminEmail } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })
  const text = await res.text()
  let data: any = null
  try { data = text ? JSON.parse(text) : null } catch { data = text }
  if (!res.ok) throw new ChatApiError(res.status, typeof data === 'string' ? data : JSON.stringify(data))
  return data as T
}

// ─── Types (mirror worker /admin/queries.ts) ────────────────────────────

export interface ConversationRow {
  id: string
  user_id: string | null
  status: 'active' | 'closed' | 'escalated'
  channel: string | null
  started_at: number
  last_active_at: number
  metadata: string | null
  msg_count: number
}

export interface MessageRow {
  id: string
  /** 'admin' added with v2 — live human replies inserted via
   *  POST /admin/conversations/:id/reply. */
  role: 'user' | 'assistant' | 'system' | 'tool' | 'admin'
  /** JSON-encoded: either a plain string or LLMBlock[]. Parse client-side. */
  content: string
  /** JSON array of {id,name,input,output} per the worker audit shape. */
  tool_calls: string | null
  /** JSON array of {source,score,text} for the RAG hits this turn. */
  rag_chunks: string | null
  input_tokens: number | null
  output_tokens: number | null
  latency_ms: number | null
  created_at: number
}

export interface EscalationRow {
  id: string
  conversation_id: string
  user_id: string | null
  reason: string | null
  reason_type: string | null
  summary: string | null
  status: 'pending' | 'assigned' | 'resolved'
  assigned_to: string | null
  created_at: number
  resolved_at: number | null
}

export interface FeedbackRow {
  id: string
  message_id: string
  conversation_id: string
  rating: number  // -1 | 0 | 1
  comment: string | null
  created_at: number
}

export interface ConversationDetail {
  conversation: ConversationRow
  messages: MessageRow[]
  escalations: EscalationRow[]
}

// ─── Endpoints ───────────────────────────────────────────────────────────

export const chatApi = {
  listConversations(query: { status?: string; user_id?: string; q?: string; limit?: number; offset?: number } = {}) {
    return req<{ count: number; conversations: ConversationRow[] }>('/admin/conversations', { query })
  },

  getConversation(id: string) {
    return req<ConversationDetail>(`/admin/conversations/${encodeURIComponent(id)}`)
  },

  addNote(conversationId: string, text: string, adminEmail: string) {
    return req<{ ok: true; note_id: string }>(
      `/admin/conversations/${encodeURIComponent(conversationId)}/note`,
      { method: 'POST', body: { text }, adminEmail },
    )
  },

  /** Send a live reply to the customer. Lands as role='admin' in DB_CHAT,
   *  surfaces in the marketing widget within ~5s via its history poll loop. */
  sendReply(conversationId: string, text: string, adminEmail: string) {
    return req<{ ok: true; message_id: string }>(
      `/admin/conversations/${encodeURIComponent(conversationId)}/reply`,
      { method: 'POST', body: { text }, adminEmail },
    )
  },

  listEscalations(query: { status?: string; assigned_to?: string; limit?: number; offset?: number } = {}) {
    return req<{ count: number; escalations: EscalationRow[] }>('/admin/escalations', { query })
  },

  assignEscalation(id: string, adminEmail: string) {
    return req<{ ok: true; escalation_id: string; assigned_to: string }>(
      `/admin/escalations/${encodeURIComponent(id)}/assign`,
      { method: 'POST', body: {}, adminEmail },
    )
  },

  resolveEscalation(id: string) {
    return req<{ ok: true; escalation_id: string }>(
      `/admin/escalations/${encodeURIComponent(id)}/resolve`,
      { method: 'POST' },
    )
  },

  listFeedbacks(query: { rating?: number; limit?: number; offset?: number } = {}) {
    return req<{ count: number; feedbacks: FeedbackRow[] }>('/admin/feedbacks', { query })
  },
}

// ─── Content parsing helpers ────────────────────────────────────────────

/** Pull the plain-text portion out of a stored `messages.content` JSON.
 *  Accepts:
 *    - "JSON-encoded string" → return as-is
 *    - block array `[{type:'text',text:'...'},...]` → join text blocks
 *    - object `{text, author}` (admin replies + system notes) → return .text
 */
export function extractMessageText(contentJson: string): string {
  try {
    const parsed = JSON.parse(contentJson)
    if (typeof parsed === 'string') return parsed
    if (Array.isArray(parsed)) {
      return parsed
        .filter((b: any) => b && b.type === 'text' && typeof b.text === 'string')
        .map((b: any) => b.text)
        .join('')
    }
    // {text, author} shape used by admin replies and notes.
    if (parsed && typeof parsed === 'object' && typeof parsed.text === 'string') {
      return parsed.text
    }
    return ''
  } catch {
    return contentJson
  }
}

/** Pull the author email from an admin/system message content blob (returns
 *  empty string when not present). Used to show "客服 <email>" labels. */
export function extractMessageAuthor(contentJson: string): string {
  try {
    const parsed = JSON.parse(contentJson)
    if (parsed && typeof parsed === 'object' && typeof parsed.author === 'string') {
      return parsed.author
    }
  } catch { /* ignore */ }
  return ''
}

/** Parse a stored `tool_calls` JSON array. Returns [] on null/parse errors. */
export function parseToolCalls(toolCallsJson: string | null): Array<{ id: string; name: string; input: unknown; output: unknown }> {
  if (!toolCallsJson) return []
  try {
    const arr = JSON.parse(toolCallsJson)
    return Array.isArray(arr) ? arr : []
  } catch { return [] }
}

/** Parse a stored `rag_chunks` JSON array. Returns [] on null/parse errors. */
export function parseRagChunks(ragChunksJson: string | null): Array<{ source: string; score: number; text?: string }> {
  if (!ragChunksJson) return []
  try {
    const arr = JSON.parse(ragChunksJson)
    return Array.isArray(arr) ? arr : []
  } catch { return [] }
}

/** Format a Unix-seconds timestamp like "今天 14:32" / "昨天 09:15" / "5/12 14:32". */
export function formatTime(unixSec: number): string {
  const d = new Date(unixSec * 1000)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const isYesterday = d.toDateString() === yesterday.toDateString()
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (sameDay) return `今天 ${hm}`
  if (isYesterday) return `昨天 ${hm}`
  return `${d.getMonth() + 1}/${d.getDate()} ${hm}`
}
